import { X } from "lucide-react";
import { RoleSummary } from "../../../../shared/api/roles";
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";

type AssignRoleModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberName: string;
  roles: RoleSummary[];
  selectedRoles: string[];
  onToggleRoleSelection: (roleName: string) => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  error?: string | null;
};

const AssignRoleModal = ({
  open,
  onOpenChange,
  memberName,
  roles,
  selectedRoles,
  onToggleRoleSelection,
  onCancel,
  onSave,
  isSaving,
  error,
}: AssignRoleModalProps) => {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader>
        <ModalTitle>Edit roles for {memberName}</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onCancel}
          disabled={isSaving}
        >
          <X size={20} />
        </button>
      </ModalHeader>
      <ModalBody className="pt-4">
        <div className="space-y-3">
          {roles.length === 0 ? (
            <div className="rounded-[16px] border border-dashed border-[#D7DCF0] p-6 text-center text-[14px] text-[#6B6F8A]">
              No roles available yet.
            </div>
          ) : (
            roles.map((roleItem) => {
              const isSelected = selectedRoles.includes(roleItem.name);
              return (
                <button
                  key={roleItem.id}
                  type="button"
                  className="flex w-full items-start gap-4 rounded-[16px] border border-[#E1E6FF] bg-white px-4 py-4 text-left transition-colors hover:bg-[#F7F8FD]"
                  onClick={() => onToggleRoleSelection(roleItem.name)}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleRoleSelection(roleItem.name)}
                    size="sm"
                    className="shrink-0"
                  />
                  <div>
                    <div className="text-[16px] font-[600] text-[#161827]">
                      {roleItem.name}
                    </div>
                    <div className="mt-1 text-[14px] text-[#6B6F8A]">
                      {roleItem.description ?? ""}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="ghost"
          className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          className="h-11 rounded-[14px] px-6"
          onClick={onSave}
          disabled={selectedRoles.length === 0 || isSaving}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
      </ModalFooter>
      {error ? (
        <div className="px-6 pb-6 text-[14px] text-[#C24E4E]">{error}</div>
      ) : null}
    </Modal>
  );
};

export default AssignRoleModal;
